import { FormArray, FormGroup } from '@angular/forms';

import type { RepeatableSubField } from '../../../interfaces/input.interface';
import { InputType } from '../../../interfaces/input.interface';
import type { RepeatableRowValue } from './repeatable-group.types';

const NULLISH_STRING_TOKENS = new Set<string>(['null', 'undefined', 'nan']);
const DEFAULT_CALENDAR_DATE_FORMAT = 'dd/mm/yy';
const TWO_DIGITS = 2;
const FULL_YEAR_DIGITS = 4;
const TWO_DIGIT_YEAR_DIVISOR = 100;

export class RepeatableValueUtils {
    public static isNullishStringToken(value: string): boolean {
        return NULLISH_STRING_TOKENS.has(value.trim().toLowerCase());
    }

    public static hasNonEmptyValue(value: unknown): boolean {
        if (value === null || value === undefined) {
            return false;
        }

        if (typeof value === 'string') {
            const normalizedValue = value.trim().toLowerCase();

            return normalizedValue !== '' && !RepeatableValueUtils.isNullishStringToken(normalizedValue);
        }

        if (Array.isArray(value)) {
            return value.length > 0;
        }

        return true;
    }

    public static splitSerializedKeepEmptiesAndSanitize(value: unknown, separator: string): string[] {
        return String(value ?? '')
            .split(separator)
            .map((item) => {
                const normalizedItem = String(item ?? '').trim();

                return normalizedItem === '' || RepeatableValueUtils.isNullishStringToken(normalizedItem) ? '' : normalizedItem;
            });
    }

    public static parseFlexibleNumber(token: string): number | null {
        const value = token.trim();

        if (value === '' || RepeatableValueUtils.isNullishStringToken(value)) {
            return null;
        }

        const lastComma = value.lastIndexOf(',');
        const lastDot = value.lastIndexOf('.');
        let normalizedValue = value;

        if (lastComma !== -1 && lastDot !== -1) {
            normalizedValue = lastComma > lastDot
                ? value.replace(/\./g, '').replace(',', '.')
                : value.replace(/,/g, '');
        } else if (lastComma !== -1) {
            normalizedValue = value.replace(',', '.');
        }

        const parsedValue = Number(normalizedValue);

        return Number.isFinite(parsedValue) ? parsedValue : null;
    }

    public static parseUnknownNumber(value: unknown): number | null {
        if (typeof value === 'number') {
            return Number.isFinite(value) ? value : null;
        }

        return RepeatableValueUtils.parseFlexibleNumber(String(value ?? ''));
    }

    public static firstFiniteNumber(values: unknown[]): number | null {
        for (const value of values) {
            const parsedValue = RepeatableValueUtils.parseUnknownNumber(value);

            if (parsedValue !== null) {
                return parsedValue;
            }
        }

        return null;
    }

    public static formatNumberForModel(value: number): string {
        return value.toFixed(2).replace('.', ',');
    }

    public static formatDoseNumber(value: number): string {
        return value.toFixed(2).replace('.', ',');
    }

    public static areEqualLoose(firstValue: unknown, secondValue: unknown): boolean {
        return String(firstValue ?? '') === String(secondValue ?? '');
    }

    public static areEquivalentCellValues(firstValue: unknown, secondValue: unknown): boolean {
        if (firstValue === secondValue) {
            return true;
        }

        return RepeatableValueUtils.normalizeComparableCellValue(firstValue) ===
            RepeatableValueUtils.normalizeComparableCellValue(secondValue);
    }

    public static isNumericLike(value: string): boolean {
        const normalizedValue = value.trim();

        return normalizedValue !== '' && !Number.isNaN(Number(normalizedValue));
    }

    public static getRowsSnapshot(rowsArray: FormArray<FormGroup>): RepeatableRowValue[] {
        const rows: RepeatableRowValue[] = [];

        for (let rowIndex = 0; rowIndex < rowsArray.length; rowIndex++) {
            rows.push({ ...(rowsArray.at(rowIndex).getRawValue() as RepeatableRowValue) });
        }

        return rows;
    }

    public static getRowsSnapshotAsStrings(
        rowsArray: FormArray<FormGroup>,
        subFields: RepeatableSubField[],
    ): Array<Record<string, string>> {
        const rows: Array<Record<string, string>> = [];
        const subFieldMap = new Map<string, RepeatableSubField>(subFields.map((subField) => [subField.key, subField]));

        for (let rowIndex = 0; rowIndex < rowsArray.length; rowIndex++) {
            const rawRow = rowsArray.at(rowIndex).getRawValue() as RepeatableRowValue;
            const serializedRow: Record<string, string> = {};

            for (const [key, value] of Object.entries(rawRow)) {
                serializedRow[key] = RepeatableValueUtils.serializeCellValue(value, subFieldMap.get(key), false);
            }

            rows.push(serializedRow);
        }

        return rows;
    }


    public static getControlValueKey(subField?: RepeatableSubField): string {
        return subField?.input?.dropdownConfiguration?.controlValueKey
            ?? subField?.input?.dropdownConfiguration?.valuePrimaryKey
            ?? 'id';
    }

    public static serializeCellValue(value: unknown, subField?: RepeatableSubField, keepInfoModeLabels = false): string {
        if (value === null || value === undefined) {
            return '';
        }

        if (keepInfoModeLabels && typeof value === 'string') {
            const normalizedValue = value.trim();

            if (normalizedValue === '' || RepeatableValueUtils.isNullishStringToken(normalizedValue)) {
                return '';
            }

            return normalizedValue;
        }

        if (typeof value === 'number') {
            return RepeatableValueUtils.formatNumberForModel(value);
        }

        if (value instanceof Date) {
            return RepeatableValueUtils.formatDateForModel(value, subField);
        }

        const primaryKey = RepeatableValueUtils.getControlValueKey(subField);

        if (Array.isArray(value)) {
            return value
                .map((item) => RepeatableValueUtils.normalizeToId(item, primaryKey))
                .filter((id): id is string => id !== null && id !== '')
                .join(',');
        }

        if (typeof value === 'object') {
            return RepeatableValueUtils.valueToStringSafe(RepeatableValueUtils.readObjectId(value, primaryKey));
        }

        return RepeatableValueUtils.valueToStringSafe(value);
    }

    public static valueToStringSafe(value: unknown): string {
        if (value === null || value === undefined) {
            return '';
        }

        const normalizedValue = String(value).trim();

        return normalizedValue === '' || RepeatableValueUtils.isNullishStringToken(normalizedValue) ? '' : normalizedValue;
    }

    public static parseIdsFromToken(raw: unknown, itemSeparator: string, primaryKey: string): string[] {
        if (raw === null || raw === undefined) {
            return [];
        }

        if (Array.isArray(raw)) {
            return raw
                .map((item) => RepeatableValueUtils.normalizeToId(item, primaryKey))
                .filter((id): id is string => id !== null && id !== '');
        }

        if (typeof raw === 'string' || typeof raw === 'number') {
            const rawText = String(raw).trim();

            if (rawText === '' || RepeatableValueUtils.isNullishStringToken(rawText)) {
                return [];
            }

            return rawText
                .split(itemSeparator)
                .map((item) => item.trim())
                .filter((item) => item !== '' && !RepeatableValueUtils.isNullishStringToken(item));
        }

        const id = RepeatableValueUtils.normalizeToId(raw, primaryKey);

        return id ? [id] : [];
    }

    private static normalizeComparableCellValue(value: unknown): string {
        if (value === null || value === undefined) {
            return '';
        }

        if (typeof value === 'object' && !(value instanceof Date)) {
            const id = RepeatableValueUtils.normalizeToId(value, 'id');

            if (id !== null) {
                return id;
            }

            return JSON.stringify(value);
        }

        return String(value).trim();
    }

    public static normalizeToId(raw: unknown, primaryKey: string): string | null {
        if (raw === null || raw === undefined) {
            return null;
        }

        if (typeof raw === 'string' || typeof raw === 'number') {
            const normalizedValue = String(raw).trim();

            return normalizedValue === '' || RepeatableValueUtils.isNullishStringToken(normalizedValue) ? null : normalizedValue;
        }

        if (typeof raw === 'object') {
            const id = RepeatableValueUtils.readObjectId(raw, primaryKey);

            return id !== null && id !== undefined ? String(id).trim() : null;
        }

        return null;
    }

    public static getOptionKey(optionValue: unknown, primaryKey: string): string {
        if (optionValue === null || optionValue === undefined) {
            return '';
        }

        if (typeof optionValue === 'object') {
            return String((optionValue as Record<string, unknown>)[primaryKey] ?? '');
        }

        return String(optionValue);
    }

    public static normalizeOptionLabel(value: unknown): string {
        return String(value ?? '')
            .normalize('NFD')
            .replace(/[\u0300-\u036f]/g, '')
            .trim()
            .replace(/\s+/g, ' ')
            .toLowerCase();
    }

    public static normalizeLabelText(raw: unknown): string {
        const normalizedValue = String(raw ?? '').trim();

        if (normalizedValue === '') {
            return '';
        }

        return normalizedValue
            .split(';')
            .map((item) => item.trim())
            .filter((item) => item !== '')
            .join(', ');
    }

    public static isSelectType(type: InputType | undefined): boolean {
        return type === InputType.DROPDOWN
            || type === InputType.DROPDOWN_SEARCH
            || type === InputType.MULTISELECT
            || type === InputType.MULTISELECT_SEARCH;
    }

    public static isMultiSelectType(type: InputType | undefined): boolean {
        return type === InputType.MULTISELECT || type === InputType.MULTISELECT_SEARCH;
    }


    private static formatDateForModel(value: Date, subField?: RepeatableSubField): string {
        if (Number.isNaN(value.getTime())) {
            return '';
        }

        const day = value.getDate();
        const month = value.getMonth() + 1;
        const fullYear = value.getFullYear();
        const twoDigitYear = fullYear % TWO_DIGIT_YEAR_DIVISOR;
        const dateFormat = subField?.input?.calendarConfiguration?.calendarDateFormat ?? DEFAULT_CALENDAR_DATE_FORMAT;

        return dateFormat
            .replace(/dd/g, RepeatableValueUtils.padNumber(day))
            .replace(/d/g, String(day))
            .replace(/mm/g, RepeatableValueUtils.padNumber(month))
            .replace(/m/g, String(month))
            .replace(/yy/g, String(fullYear).padStart(FULL_YEAR_DIGITS, '0'))
            .replace(/y/g, RepeatableValueUtils.padNumber(twoDigitYear));
    }

    private static padNumber(value: number): string {
        return String(value).padStart(TWO_DIGITS, '0');
    }

    private static readObjectId(raw: unknown, primaryKey: string): unknown {
        const objectValue = raw as Record<string, unknown>;
        const wrappedValue = objectValue['value'];

        if (wrappedValue !== null && wrappedValue !== undefined && typeof wrappedValue === 'object') {
            const innerValue = wrappedValue as Record<string, unknown>;

            return innerValue[primaryKey] ?? innerValue['id'];
        }

        return objectValue[primaryKey] ?? objectValue['id'];
    }
}
